import { ShieldPlus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";
import { useFriendlyAppName } from "../../lib/apps/useFriendlyAppName";
import type { SmartBlockSuggestion } from "../../lib/insights/insightScoring";
import {
  isConformingSmartBlockSelection,
  selectionTotalCount,
  toTargetSelection,
} from "../../lib/insights/smartBlockSelection";
import { presentAppPicker } from "../../lib/native/rules";
import type { TargetSelection } from "../../lib/rules/ruleTypes";
import { BottomSheet } from "../habits/BottomSheet";

type IosActivationStep =
  | "pre_picker"
  | "confirm_honor"
  | "declined_honor"
  | "non_conforming";

export type SmartBlockActivationResult = {
  ruleId: string;
  targetId: string;
  created: boolean;
  reactivated: boolean;
  reusedTarget: boolean;
};

type SmartBlockIosActivationFlowProps = {
  suggestion: SmartBlockSuggestion | null;
  busy: boolean;
  onActivate: (args: {
    selection: TargetSelection;
    asSmartBlock: boolean;
    resolvedAppName: string;
  }) => Promise<void>;
  onClose: () => void;
};

/**
 * iOS-only multi-step Smart Block activation: pre-picker sheet → native picker
 * (with instructional header) → honor-system confirmation → optional non-conforming
 * warning → mutation. Picker cancel aborts silently with no side effects.
 *
 * Each picker attempt uses a fresh `selectionId` so stale native storage from a
 * prior dismiss does not leak into the saved target.
 */
export function SmartBlockIosActivationFlow({
  suggestion,
  busy,
  onActivate,
  onClose,
}: SmartBlockIosActivationFlowProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const appName = useFriendlyAppName({
    displayName: suggestion?.displayName,
    appIdentifier: suggestion?.appIdentifier,
    category: suggestion?.category,
  });
  const isReactivate = suggestion?.mode === "reactivate";
  const [step, setStep] = useState<IosActivationStep | null>(null);
  const [selection, setSelection] = useState<TargetSelection | null>(null);
  const [pickerBusy, setPickerBusy] = useState(false);

  useEffect(() => {
    if (suggestion) {
      setStep("pre_picker");
      setSelection(null);
    } else {
      setStep(null);
      setSelection(null);
    }
  }, [suggestion]);

  const pickerTexts = suggestion
    ? {
        title: formatTemplate(t.insight.smartBlockPickerTitle, { appName }),
        headerText: formatTemplate(t.insight.smartBlockPickerHeader, { appName }),
        footerText: formatTemplate(t.insight.smartBlockPickerFooter, { appName }),
      }
    : undefined;

  const openPicker = useCallback(async () => {
    if (!suggestion || pickerBusy) return;
    setPickerBusy(true);
    setStep(null);
    try {
      const selectionId = crypto.randomUUID();
      const result = await presentAppPicker(selectionId, pickerTexts ?? {});
      if (result.canceled || selectionTotalCount(result) === 0) {
        onClose();
        return;
      }
      setSelection(toTargetSelection(selectionId, result));
      setStep("confirm_honor");
    } finally {
      setPickerBusy(false);
    }
  }, [onClose, pickerBusy, pickerTexts, suggestion]);

  const handleHonorYes = async () => {
    if (!selection || !suggestion) return;
    if (isConformingSmartBlockSelection(selection)) {
      await onActivate({ selection, asSmartBlock: true, resolvedAppName: appName });
      return;
    }
    setStep("non_conforming");
  };

  const handleNonConformingContinue = async () => {
    if (!selection) return;
    await onActivate({ selection, asSmartBlock: false, resolvedAppName: appName });
  };

  const sheetOpen = step !== null && suggestion !== null;
  const sheetBusy = busy || pickerBusy;

  return (
    <>
      {step === "pre_picker" && suggestion ? (
        <BottomSheet open={sheetOpen} onClose={onClose} labelledBy="smart-block-pre-picker-title">
          <div className="grid gap-4 px-4 pt-4 pb-2">
            <div className="grid gap-1.5">
              <h2 id="smart-block-pre-picker-title" className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
                {isReactivate ? t.insight.smartBlockPrePickerReactivateTitle : t.insight.smartBlockPrePickerTitle}
              </h2>
              <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
                {formatTemplate(
                  isReactivate ? t.insight.smartBlockPrePickerReactivateBody : t.insight.smartBlockPrePickerBody,
                  { appName },
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="secondary-action flex-1">
                {t.common.cancel}
              </button>
              <button
                type="button"
                onClick={() => void openPicker()}
                className="primary-action flex-1"
                disabled={sheetBusy}
              >
                {t.insight.smartBlockPrePickerNext}
              </button>
            </div>
          </div>
        </BottomSheet>
      ) : null}

      {step === "confirm_honor" && suggestion ? (
        <BottomSheet open={sheetOpen} onClose={onClose} labelledBy="smart-block-honor-title">
          <div className="grid gap-4 px-4 pt-4 pb-2">
            <div className="grid gap-1.5">
              <h2 id="smart-block-honor-title" className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
                {formatTemplate(t.insight.smartBlockHonorTitle, { appName })}
              </h2>
              <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
                {t.insight.smartBlockHonorBody}
              </p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setStep("declined_honor")} className="secondary-action flex-1" disabled={sheetBusy}>
                {t.insight.smartBlockHonorNo}
              </button>
              <button type="button" onClick={() => void handleHonorYes()} className="primary-action flex-1" disabled={sheetBusy}>
                <ShieldPlus size={16} aria-hidden />
                {t.insight.smartBlockHonorYes}
              </button>
            </div>
          </div>
        </BottomSheet>
      ) : null}

      {step === "declined_honor" && suggestion ? (
        <BottomSheet open={sheetOpen} onClose={onClose} labelledBy="smart-block-declined-title">
          <div className="grid gap-4 px-4 pt-4 pb-2">
            <div className="grid gap-1.5">
              <h2 id="smart-block-declined-title" className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
                {formatTemplate(t.insight.smartBlockDeclinedTitle, { appName })}
              </h2>
              <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
                {formatTemplate(t.insight.smartBlockDeclinedBody, { appName })}
              </p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="secondary-action flex-1">
                {t.common.cancel}
              </button>
              <button type="button" onClick={() => void openPicker()} className="primary-action flex-1" disabled={sheetBusy}>
                {t.insight.smartBlockRetryPicker}
              </button>
            </div>
          </div>
        </BottomSheet>
      ) : null}

      {step === "non_conforming" && suggestion && selection ? (
        <BottomSheet open={sheetOpen} onClose={onClose} labelledBy="smart-block-nonconforming-title">
          <div className="grid gap-4 px-4 pt-4 pb-2">
            <div className="grid gap-1.5">
              <h2 id="smart-block-nonconforming-title" className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
                {t.insight.smartBlockNonConformingTitle}
              </h2>
              <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
                {formatTemplate(t.insight.smartBlockNonConformingBody, {
                  appName,
                  appCount: selection.applicationCount,
                  categoryCount: selection.categoryCount,
                  siteCount: selection.webDomainCount,
                })}
              </p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="secondary-action flex-1">
                {t.common.cancel}
              </button>
              <button
                type="button"
                onClick={() => void handleNonConformingContinue()}
                className="primary-action flex-1"
                disabled={sheetBusy}
              >
                {t.insight.smartBlockNonConformingContinue}
              </button>
            </div>
          </div>
        </BottomSheet>
      ) : null}
    </>
  );
}
